import { FetchErrorCodes, StatusCodes } from '@e3dc-react/shell/Libs'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import axios from 'axios'
import { ApiResult } from 'src/Libs/api'
import { UnexpectedApiResponseError } from 'src/Libs/errors'
import { AuthFetchWithResponse } from 'src/Types/api'
import {
  DynamicEmsDataDto,
  DynamicEmsDataModel,
  DynamicEmsSettingsDto,
  DynamicEmsSettingsModel,
  UpdateDynamicEmsSettingsDto,
  UpdateDynamicEmsSettingsModel,
} from '../types'
import { fromDynamicEmsDtoToModel, fromUpdateDynamicEmsModelToDto } from './dto/dynamic-price-settings.dto'
import { fromDynamicEmsDataDtoToModel } from './dto/dynamic-ems-data.dto'

const isDynamicEmsNotActivatedError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false
  const { response } = error as AuthFetchError
  return response?.status === StatusCodes.BAD_REQUEST && response.data?.errno === FetchErrorCodes.DynamicEmsNotActivated
}

/**
 * Fetches the dynamic EMS settings for a specific installation.
 * Resolves with `null` (no error) when dynamic EMS is not activated for the installation.
 */
export const getDynamicEmsSettings = async (
  installationId: string | undefined,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<DynamicEmsSettingsModel, boolean>> => {
  if (!installationId) return new ApiResult<DynamicEmsSettingsModel, boolean>(null, false, false)

  try {
    const response = await authFetch<DynamicEmsSettingsDto>({
      path: `installations/${installationId}/dynamic-ems`,
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    })

    if (response.status === StatusCodes.OK) {
      const mappedResponse: DynamicEmsSettingsModel = fromDynamicEmsDtoToModel(response.data)
      return new ApiResult<DynamicEmsSettingsModel, boolean>(mappedResponse, false, false)
    }
    return new ApiResult<DynamicEmsSettingsModel, boolean>(null, false, false)
  } catch (error) {
    if (isDynamicEmsNotActivatedError(error)) return new ApiResult<DynamicEmsSettingsModel, boolean>(null, false, false)
    console.error('Failed to fetch dynamic EMS settings:', error)
    return new ApiResult<DynamicEmsSettingsModel, boolean>(null, true, false)
  }
}

export const getDynamicEmsData = async (
  installationId: string | undefined,
  authFetch: AuthFetchWithResponse,
  start?: string,
  end?: string
): Promise<ApiResult<DynamicEmsDataModel[], boolean>> => {
  if (!installationId) return new ApiResult<DynamicEmsDataModel[], boolean>(null, false, false)
  try {
    const response = await authFetch<DynamicEmsDataDto[]>({
      path: `installations/${installationId}/dynamic-ems/periods`,
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      method: 'GET',
      payload: { start, end },
    })

    if (response.status === StatusCodes.OK) {
      const mappedResponse: DynamicEmsDataModel[] = fromDynamicEmsDataDtoToModel(response.data)
      return new ApiResult<DynamicEmsDataModel[], boolean>(mappedResponse, false, false)
    }
    return new ApiResult<DynamicEmsDataModel[], boolean>(null, false, false)
  } catch (error) {
    console.error('Failed to fetch dynamic EMS data:', error)
    return new ApiResult<DynamicEmsDataModel[], boolean>(null, true, false)
  }
}

/**
 * Updates the dynamic EMS settings for a specific installation.
 */
export const updateDynamicEmsSettings = async (
  installationId: string | undefined,
  newValues: UpdateDynamicEmsSettingsModel,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<UpdateDynamicEmsSettingsModel, boolean>> => {
  if (!installationId) return new ApiResult<UpdateDynamicEmsSettingsModel, boolean>(null, false, false)

  try {
    const payload = fromUpdateDynamicEmsModelToDto(newValues)
    const response = await authFetch<UpdateDynamicEmsSettingsDto>({
      method: 'PUT',
      path: `installations/${installationId}/dynamic-ems`,
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      payload,
    })

    if (response.status !== StatusCodes.NO_CONTENT) {
      throw new UnexpectedApiResponseError(response.status)
    }
  } catch (error) {
    console.error('Failed to update dynamic EMS data:', error)
    return new ApiResult<UpdateDynamicEmsSettingsModel, boolean>(null, true, false)
  }
  return new ApiResult<UpdateDynamicEmsSettingsModel, boolean>(null, false, false)
}
